/**
 * A4 / v2 servicing Slice 7: borrower's request history + conversation
 * threads + the entry point to RequestModal. Reads from `messages` (the
 * thread), not from the legacy `message`/`staff_response` fields.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  customerPortalApi,
  PortalRequest,
  PortalRequestType,
} from '../../../services/customerPortalApi';
import { PORTAL_COPY, PortalLanguage } from '../portalCopy';
import RequestModal from './RequestModal';
import RequestThread from './RequestThread';

interface Props {
  accountUuid: string;
  language?: PortalLanguage;
}

const STATUS_STYLES: Record<string, string> = {
  open: 'bg-blue-100 text-blue-800',
  in_review: 'bg-yellow-100 text-yellow-800',
  resolved: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-800',
};

const MyRequestsCard: React.FC<Props> = ({ accountUuid, language = 'en' }) => {
  const copy = PORTAL_COPY[language];
  const [requests, setRequests] = useState<PortalRequest[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [openType, setOpenType] = useState<PortalRequestType>('contact');
  const [showArchived, setShowArchived] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const load = useCallback(() => {
    customerPortalApi
      .getRequests(showArchived)
      .then(setRequests)
      .catch(() => setRequests([]));
  }, [showArchived]);

  useEffect(load, [load]);

  const statusLabel = (s: string) =>
    s === 'open'
      ? copy.requestStatusOpen
      : s === 'in_review'
        ? copy.requestStatusInReview
        : s === 'resolved'
          ? copy.requestStatusResolved
          : copy.requestStatusClosed;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-900">{copy.requestsTitle}</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setOpenType('promise_to_pay');
              setModalOpen(true);
            }}
            className="px-3 py-1.5 border border-blue-600 text-blue-600 text-sm font-medium rounded-lg hover:bg-blue-50"
          >
            {copy.requestsPromiseCta}
          </button>
          <button
            onClick={() => {
              setOpenType('contact');
              setModalOpen(true);
            }}
            className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
          >
            {copy.requestsNew}
          </button>
        </div>
      </div>
      <label className="flex items-center gap-2 px-6 py-2 text-xs text-gray-500 border-b border-gray-100">
        <input
          type="checkbox"
          checked={showArchived}
          onChange={(e) => setShowArchived(e.target.checked)}
          className="rounded border-gray-300"
        />
        {copy.threadShowArchived}
      </label>
      {requests.length === 0 ? (
        <div className="p-6 text-center text-gray-500">{copy.requestsEmpty}</div>
      ) : (
        <ul className="divide-y divide-gray-200">
          {requests.map((r) => (
            <li key={r.id} className="px-6 py-4 space-y-1">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-gray-900 flex items-center gap-2">
                  {r.subject}
                  {r.unread && (
                    <span
                      aria-label={copy.threadUnreadBadge}
                      className="inline-block w-2 h-2 rounded-full bg-blue-600"
                    />
                  )}
                </p>
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[r.status]}`}
                >
                  {statusLabel(r.status)}
                </span>
              </div>
              <p className="text-xs text-gray-400">
                {copy.requestSubmittedLabel} {new Date(r.created_at).toLocaleDateString()}
                {r.proposed_promise_date && (
                  <>
                    {' • '}
                    {copy.requestPromiseDateLabel}:{' '}
                    {new Date(r.proposed_promise_date + 'T00:00:00').toLocaleDateString()}
                  </>
                )}
              </p>
              <button
                type="button"
                onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}
                className="text-xs font-medium text-blue-600 hover:text-blue-800"
              >
                {expandedId === r.id ? copy.threadHideConversation : copy.threadViewConversation}
              </button>
              {expandedId === r.id && (
                <RequestThread
                  request={r}
                  language={language}
                  onArchiveToggle={load}
                  onViewed={load}
                  onMessageSent={load}
                />
              )}
            </li>
          ))}
        </ul>
      )}
      <RequestModal
        accountUuid={accountUuid}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmitted={load}
        language={language}
        initialType={openType}
      />
    </div>
  );
};

export default MyRequestsCard;
